import { useState, useEffect } from "react";
import { api } from "../../context";

export default function Demographics({ t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const CARD=t.card,BORDER=t.border,TEXT=t.text,SUB=t.sub,PRI=t.pri,GRN=t.grn,RED=t.red,GOLD=t.gold;

  useEffect(() => {
    api("/admin/dashboard").then(r => {
      if (r.ok) setData(r.data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div style={{ padding: 40, color: SUB, textAlign: "center" }}>⏳ Loading demographics...</div>;

  const fmt = (n) => Number(n || 0).toLocaleString();

  const genderColors   = { Male: "#4f8ef7", Female: "#e91e8c", "Non-binary": "#8b5cf6", Other: GOLD };
  const orientColors   = { gay: "#e91e8c", lesbian: "#f59e0b", bisexual: "#8b5cf6", straight: "#4f8ef7", heterosexual: "#4f8ef7", other: SUB };
  const ageColors      = ["#4f8ef7", "#10b981", "#f59e0b", "#e91e8c", "#8b5cf6"];

  const genderTotal      = (data?.gender || []).reduce((a, g) => a + (g.count || 0), 0);
  const orientTotal      = (data?.orientation || []).reduce((a, o) => a + (o.count || 0), 0);
  const ageTotal         = (data?.age_groups || []).reduce((a, g) => a + (g.count || 0), 0);
  const locationTotal    = (data?.locations || []).reduce((a, l) => a + (l.count || 0), 0);

  const PieSlice = ({ items, total, colors, label }) => {
    if (!items?.length) return <div style={{ textAlign: "center", padding: "20px 0", color: SUB, fontSize: 13 }}>No data yet</div>;
    return (
      <div>
        {items.map((item, i) => {
          const key = item.gender || item.orientation || item.age_group || item.location || "";
          const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
          const color = colors[key?.toLowerCase()] || ageColors[i % ageColors.length];
          return (
            <div key={i} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 5, background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: TEXT, textTransform: "capitalize" }}>{key || "Unknown"}</span>
                </div>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 800, color }}>{pct}%</span>
                  <span style={{ fontSize: 11, color: SUB, marginLeft: 6 }}>({fmt(item.count)})</span>
                </div>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: BORDER }}>
                <div style={{ height: 8, borderRadius: 4, background: color, width: `${pct}%`, transition: "width 0.8s" }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: TEXT, marginBottom: 4 }}>🌍 Demographics</div>
      <div style={{ fontSize: 12, color: SUB, marginBottom: 24 }}>Who your users are — gender, sexuality, age and location</div>

      {/* Summary stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total Members", value: fmt(data?.total_users), color: PRI },
          { label: "Verified", value: fmt(data?.members?.filter(m => m.is_verified).length || 0), color: GRN },
          { label: "Avg Age", value: data?.members?.length ? Math.round(data.members.filter(m => m.age).reduce((a, m) => a + (m.age || 0), 0) / (data.members.filter(m => m.age).length || 1)) + " yrs" : "—", color: GOLD },
          { label: "Locations", value: (data?.locations || []).length, color: "#8b5cf6" },
        ].map((s, i) => (
          <div key={i} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 20px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: SUB, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Gender + Orientation */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>⚧ Gender Breakdown</div>
          <PieSlice items={data?.gender} total={genderTotal} colors={genderColors} />
          {!data?.gender?.length && <div style={{ textAlign: "center", padding: "20px 0", color: SUB, fontSize: 13 }}>No gender data yet — users need to fill their profiles</div>}
        </div>

        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>🏳️‍🌈 Sexual Orientation</div>
          <PieSlice items={data?.orientation} total={orientTotal} colors={orientColors} />
          {!data?.orientation?.length && <div style={{ textAlign: "center", padding: "20px 0", color: SUB, fontSize: 13 }}>No orientation data yet — users need to fill their profiles</div>}
        </div>
      </div>

      {/* Age groups + Location */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>🎂 Age Groups</div>
          {!data?.age_groups?.length
            ? <div style={{ textAlign: "center", padding: "20px 0", color: SUB, fontSize: 13 }}>No age data yet</div>
            : data.age_groups.map((g, i) => {
              const pct = ageTotal > 0 ? Math.round(g.count / ageTotal * 100) : 0;
              const color = ageColors[i % ageColors.length];
              return (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{g.age_group}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color }}>{pct}% ({fmt(g.count)})</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: BORDER }}>
                    <div style={{ height: 8, borderRadius: 4, background: color, width: `${pct}%`, transition: "width 0.8s" }} />
                  </div>
                </div>
              );
            })
          }
        </div>

        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 16 }}>📍 Top Locations</div>
          {!data?.locations?.length
            ? <div style={{ textAlign: "center", padding: "20px 0", color: SUB, fontSize: 13 }}>No location data yet</div>
            : data.locations.slice(0, 10).map((l, i) => {
              const pct = locationTotal > 0 ? Math.round(l.count / locationTotal * 100) : 0;
              const color = ageColors[i % ageColors.length];
              return (
                <div key={i} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: TEXT }}>📍 {l.location}</span>
                    <span style={{ fontSize: 13, fontWeight: 800, color }}>{pct}% ({fmt(l.count)})</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: BORDER }}>
                    <div style={{ height: 6, borderRadius: 3, background: color, width: `${pct}%`, transition: "width 0.8s" }} />
                  </div>
                </div>
              );
            })
          }
        </div>
      </div>

      {/* Full members table */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ padding: "16px 24px", borderBottom: `1px solid ${BORDER}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>👥 Member Details ({data?.members?.length || 0} shown)</div>
          <div style={{ fontSize: 11, color: SUB, marginTop: 4 }}>Full demographic breakdown per member</div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: t.hover, borderBottom: `2px solid ${BORDER}` }}>
                {["Name", "Plan", "Gender", "Orientation", "Age", "Location", "Trust", "Joined", "Last Active"].map(h => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: SUB, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.members || []).map((u, i, arr) => (
                <tr key={u.id} style={{ borderBottom: i < arr.length - 1 ? `1px solid ${BORDER}` : "none", background: i % 2 === 0 ? "transparent" : `${BORDER}15` }}>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 14, background: `linear-gradient(135deg, ${PRI}, #a23ad1)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>{u.avatar_emoji || "👤"}</div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: TEXT }}>{u.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: u.plan === "elite" ? `${GOLD}20` : u.plan === "premium" ? `${GRN}20` : `${BORDER}`, color: u.plan === "elite" ? GOLD : u.plan === "premium" ? GRN : SUB }}>
                      {u.plan || "free"}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px", color: SUB, textTransform: "capitalize" }}>{u.gender || "—"}</td>
                  <td style={{ padding: "10px 14px", color: SUB, textTransform: "capitalize" }}>{u.orientation || "—"}</td>
                  <td style={{ padding: "10px 14px", color: SUB }}>{u.age || "—"}</td>
                  <td style={{ padding: "10px 14px", color: SUB }}>{u.location || "—"}</td>
                  <td style={{ padding: "10px 14px", fontWeight: 700, color: GOLD }}>{u.trust_score}</td>
                  <td style={{ padding: "10px 14px", color: SUB, fontSize: 11 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td style={{ padding: "10px 14px", color: SUB, fontSize: 11 }}>{u.last_active_at ? new Date(u.last_active_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}